import { AfterViewInit, Component, Inject, OnDestroy } from '@angular/core';
import {
	MatDatepickerInputEvent,
	MatDatepickerInput,
	MatDatepickerToggle,
	MatDatepicker,
} from '@angular/material/datepicker';
import {
	MatDialogRef,
	MAT_DIALOG_DATA,
	MatDialogContent,
	MatDialogActions,
} from '@angular/material/dialog';
import { Router } from '@angular/router';
import { InventoryReportFilterOptions } from 'src/app/models/data/reports';
import { UserProfile } from 'src/app/models/model';
import {
	ApiService,
	QueryParam,
	ApiLoadingKeys,
} from 'src/app/services/api.services';
import { Device, DeviceGroup } from 'src/app/models/data/device';
import {
	BehaviorSubject,
	Observable,
	of,
	interval,
	debounce,
	mergeMap,
	Subscription,
	from,
	map,
} from 'rxjs';
import { Client } from '../../../models/client';
import { UserProfileService } from '../../../services/user-profile.service';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { LoadingIndicatorComponent } from '../../../components/loading-indicator/loading-indicator.component';
import { MultiSelectTypeaheadComponent } from '../../../components/multi-select-typeahead/multi-select-typeahead.component';
import { MatDivider } from '@angular/material/list';
import {
	MatFormField,
	MatLabel,
	MatSuffix,
} from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { NgClass } from '@angular/common';

export type InventoryReportFilters = {
	deviceIds?: Device[];
	firstName?: Client[];
	lastName?: Client[];
	clientIdn?: Client[];
	propertyNames?: { propertyName: string }[];
	installationNames?: { locationInstallationName: string }[];
	postalCodes?: { postalCode: string }[];
	statuses?: { status: string }[];
	startDate?: Date;
	endDate?: Date;
	deviceGroupIds?: DeviceGroup[];
	showAdvancedFilters?: boolean;
};

export const emptyInventoryReportFilters = (): InventoryReportFilters => ({
	deviceIds: [],
	firstName: [],
	lastName: [],
	clientIdn: [],
	propertyNames: [],
	statuses: [],
	installationNames: [],
	postalCodes: [],
	startDate: undefined,
	endDate: undefined,
	deviceGroupIds: [],
	showAdvancedFilters: false,
});

interface SearchConfig {
	type:
		| 'device'
		| 'firstName'
		| 'lastName'
		| 'clientIdn'
		| 'property'
		| 'installationLocation'
		| 'postalCode'
		| 'status'
		| 'deviceGroup';
	searchEvents: BehaviorSubject<string>;
	subscription: Subscription;
	selectedItems: Set<any>;
	options: any[];
	apiCall: (params: QueryParam[]) => Promise<any>;
	mapResponse: (item: any) => any;
	updateFilters: (items: any[]) => void;
}

const STATUS_OPTIONS = [{ status: 'inService' }, { status: 'available' }];

@Component({
	selector: 'app-inventory-report-dialog',
	templateUrl: './inventory-report-dialog.component.html',
	styleUrls: [
		'./inventory-report-dialog.component.scss',
		'../../common/main.scss',
	],
	imports: [
		MatIconButton,
		MatIcon,
		CdkScrollable,
		MatDialogContent,
		LoadingIndicatorComponent,
		MultiSelectTypeaheadComponent,
		MatDivider,
		MatFormField,
		MatLabel,
		MatInput,
		MatDatepickerInput,
		FormsModule,
		MatDatepickerToggle,
		MatSuffix,
		MatDatepicker,
		NgClass,
		MatDialogActions,
		MatButton,
	],
})
export class InventoryReportDialogComponent
	implements AfterViewInit, OnDestroy
{
	inventoryReportFilters: InventoryReportFilters =
		emptyInventoryReportFilters();

	filterOptions?: InventoryReportFilterOptions;

	apiLoadingKeys = ApiLoadingKeys;

	deviceOptions?: { id?: string; serialNumber?: string; status?: string }[] =
		[];
	firstNameOptions?: Client[] = [];
	lastNameOptions?: Client[] = [];
	clientIdnOptions?: Client[] = [];
	propertiesOptions?: { name: string }[] = [];
	locationInstallationOptions?: { locationInstallationName?: string }[] = [];
	postalCodeOptions?: { postalCode?: string }[] = [];
	statusOptions?: { status?: string }[] = [];
	deviceGroupOptions?: { id?: string; name?: string }[] = [];

	activeCompanyId = '';
	isSentrilock = false;
	isCareCompany = false;

	deviceId: string[] = [];
	firstName: string[] = [];
	lastName: string[] = [];
	clientIdn: string[] = [];
	propertyIds: string[] = [];
	statuses: string[] = [];
	installationNames: string[] = [];
	postalCodes: string[] = [];
	deviceGroupIds: string[] = [];

	deviceMultiSelectSearchPlaceholder = $localize`:@@inventoryReportDialog-deviceMultiSelectSearchPlaceholder: Search Serial Numbers (optional)`;
	firstNameMultiSelectSearchPlaceholder = $localize`:@@inventoryReportDialog-firstNameMultiSelectSearchPlaceholder: Search Client First Names (optional)`;
	lastNameMultiSelectSearchPlaceholder = $localize`:@@inventoryReportDialog-lastNameMultiSelectSearchPlaceholder: Search Client Last Names (optional)`;
	clientIdnMultiSelectSearchPlaceholder = $localize`:@@inventoryReportDialog-clientIdnMultiSelectSearchPlaceholder: Search Client IDNs (optional)`;
	devicePropertyMultiSelectSearchPlaceholder = $localize`:@@inventoryReportDialog-propertyMultiSelectSearchPlaceholder: Search Properties (optional)`;
	installationLocationMultiSelectSearchPlaceholder = $localize`:@@inventoryReportDialog-installationLocationMultiSelectSearchPlaceholder: Search Installation Locations (optional)`;
	postalCodeMultiSelectSearchPlaceholder = $localize`:@@inventoryReportDialog-postalCodeMultiSelectSearchPlaceholder: Search Postal Codes (optional)`;
	generatedByMultiSelectSearchPlaceholder = $localize`:@@inventoryReportDialog-generatedByMultiSelectSearchPlaceholder: Search Users (optional)`;
	statusPlaceHolder = $localize`:@@inventoryReportDialog-userNameOrEmailPlaceHolder: Status (optional)`;
	deviceGroupMultiSelectSearchPlaceholder = $localize`:@@inventoryReportDialog-deviceGroupMultiSelectSearchPlaceholder: Search Device Groups (optional)`;

	private readonly searchConfigs: { [key: string]: SearchConfig } = {
		device: {
			type: 'device',
			searchEvents: new BehaviorSubject<string>(''),
			subscription: new Subscription(),
			selectedItems: new Set(),
			options: this.deviceOptions || [],
			apiCall: (params) => this.apiService.getAllDevices(params),
			mapResponse: (device) => ({
				id: device.device.id,
				serialNumber: device.device.serialNumber,
			}),
			updateFilters: (items) => {
				this.inventoryReportFilters.deviceIds = [...items];
			},
		},
		firstName: {
			type: 'firstName',
			searchEvents: new BehaviorSubject<string>(''),
			subscription: new Subscription(),
			selectedItems: new Set(),
			options: this.firstNameOptions || [],
			apiCall: (params) =>
				this.apiService.getClients(this.activeCompanyId, params),
			mapResponse: (client) => client,
			updateFilters: (items) => {
				this.inventoryReportFilters.firstName = [...items];
			},
		},
		lastName: {
			type: 'lastName',
			searchEvents: new BehaviorSubject<string>(''),
			subscription: new Subscription(),
			selectedItems: new Set(),
			options: this.lastNameOptions || [],
			apiCall: (params) =>
				this.apiService.getClients(this.activeCompanyId, params),
			mapResponse: (client) => client,
			updateFilters: (items) => {
				this.inventoryReportFilters.lastName = [...items];
			},
		},
		clientIdn: {
			type: 'clientIdn',
			searchEvents: new BehaviorSubject<string>(''),
			subscription: new Subscription(),
			selectedItems: new Set(),
			options: this.clientIdnOptions || [],
			apiCall: (params) =>
				this.apiService.getClients(this.activeCompanyId, params),
			mapResponse: (client) => client,
			updateFilters: (items) => {
				this.inventoryReportFilters.clientIdn = [...items];
			},
		},
		property: {
			type: 'property',
			searchEvents: new BehaviorSubject<string>(''),
			subscription: new Subscription(),
			selectedItems: new Set(),
			options: this.propertiesOptions || [],
			apiCall: (params) => this.apiService.getProperties(params),
			mapResponse: (property) => ({
				propertyName: property.property.name,
			}),
			updateFilters: (items) => {
				this.inventoryReportFilters.propertyNames = [...items];
			},
		},
		installationLocation: {
			type: 'installationLocation',
			searchEvents: new BehaviorSubject<string>(''),
			subscription: new Subscription(),
			selectedItems: new Set(),
			options: this.locationInstallationOptions || [],
			apiCall: (params) =>
				this.apiService.getInstallationLocationNameFilterOptions(params),
			mapResponse: (location) => ({
				locationInstallationName: location,
			}),
			updateFilters: (items) => {
				this.inventoryReportFilters.installationNames = [...items];
			},
		},
		postalCode: {
			type: 'postalCode',
			searchEvents: new BehaviorSubject<string>(''),
			subscription: new Subscription(),
			selectedItems: new Set(),
			options: this.postalCodeOptions || [],
			apiCall: (params) => this.apiService.getPostalFilterOptions(params),
			mapResponse: (postal) => ({
				postalCode: postal.postalCode,
			}),
			updateFilters: (items) => {
				this.inventoryReportFilters.postalCodes = [...items];
			},
		},
		status: {
			type: 'status',
			searchEvents: new BehaviorSubject<string>(''),
			subscription: new Subscription(),
			selectedItems: new Set(),
			options: STATUS_OPTIONS,
			apiCall: () => Promise.resolve(STATUS_OPTIONS),
			mapResponse: (status) => status,
			updateFilters: (items) => {
				this.inventoryReportFilters.statuses = [...items];
			},
		},
		deviceGroup: {
			type: 'deviceGroup',
			searchEvents: new BehaviorSubject<string>(''),
			subscription: new Subscription(),
			selectedItems: new Set(),
			options: this.deviceGroupOptions || [],
			apiCall: (params) => this.apiService.getDeviceGroupFilterOptions(params),
			mapResponse: (dg) => ({
				id: dg.id,
				name: dg.name,
			}),
			updateFilters: (items) => {
				this.inventoryReportFilters.deviceGroupIds = [...items];
			},
		},
	};

	constructor(
		private readonly dialogRef: MatDialogRef<InventoryReportDialogComponent>,
		private readonly apiService: ApiService,
		private readonly userProfileService: UserProfileService,
		private readonly router: Router,
		@Inject(MAT_DIALOG_DATA)
		public data?: {
			reportFilters: InventoryReportFilters;
			filterOptions: InventoryReportFilterOptions;
		}
	) {
		this.initializeData();
		Object.keys(this.searchConfigs).forEach((key) => this.setupSearch(key));
	}

	private initializeData(): void {
		this.filterOptions = this.data?.filterOptions;

		if (this.data?.reportFilters) {
			this.inventoryReportFilters = this.data.reportFilters;
			// Initialize deviceOptions with previous selections
			if (this.inventoryReportFilters.deviceIds?.length) {
				this.inventoryReportFilters.deviceIds.forEach((device) => {
					const deviceOption = {
						id: device.id,
						serialNumber: device.serialNumber,
					};
					this.deviceId.push(device.id);
					this.searchConfigs.device.selectedItems.add(deviceOption);
				});
				this.deviceOptions = Array.from(
					this.searchConfigs.device.selectedItems
				);
			}

			// Initialize firstNameOptions with previous selections
			if (this.inventoryReportFilters.firstName?.length) {
				this.inventoryReportFilters.firstName.forEach((firstname) => {});
			}

			// Initialize lastNameOptions with previous selections
			if (this.inventoryReportFilters.lastName?.length) {
				this.inventoryReportFilters.lastName.forEach((lastname) => {});
			}

			// Initialize clientIdnOptions with previous selections
			if (this.inventoryReportFilters.clientIdn?.length) {
				this.inventoryReportFilters.clientIdn.forEach((idn) => {});
			}

			// Initialize propertyOptions
			if (this.inventoryReportFilters.propertyNames?.length) {
				this.inventoryReportFilters.propertyNames.forEach((property) => {
					const propertyOption = { propertyName: property.propertyName };
					this.propertyIds.push(property.propertyName);
					this.searchConfigs.property.selectedItems.add(propertyOption);
				});
				this.propertiesOptions = Array.from(
					this.searchConfigs.property.selectedItems
				);
			}

			// Initialize device group options
			if (this.inventoryReportFilters.deviceGroupIds?.length) {
				this.inventoryReportFilters.deviceGroupIds.forEach((dg) => {
					const deviceGroupOption = {
						id: dg.id,
						name: dg.name,
					};
					this.deviceGroupIds.push(dg.id);
					this.searchConfigs.deviceGroup.selectedItems.add(deviceGroupOption);
				});
				this.deviceGroupOptions = Array.from(
					this.searchConfigs.deviceGroup.selectedItems
				);
			}

			this.statusOptions = STATUS_OPTIONS;
			this.inventoryReportFilters.showAdvancedFilters =
				!!this.inventoryReportFilters.showAdvancedFilters;
		}

		this.checkCareCompany()
			.then(() => {})
			.catch((error) =>
				console.error(
					'Something went wrong while checking care company:',
					error.message
				)
			);
	}

	async checkCareCompany(): Promise<void> {
		const userProfile = await this.userProfileService.getUserProfile();
		this.isCareCompany =
			userProfile?.activeRoleDetails?.company?.companyType === 'care';
	}

	private setupSearch(type: string): void {
		const config = this.searchConfigs[type];
		const searchResults = config.searchEvents.asObservable().pipe(
			debounce(() => interval(300)),
			mergeMap((searchTerm) => this.handleSearch(searchTerm, config))
		);

		config.subscription = searchResults.subscribe();
	}

	private handleSearch(
		searchTerm: string,
		config: SearchConfig
	): Observable<any[]> {
		// Special case for status - always show all options
		if (config.type === 'status') {
			return of(STATUS_OPTIONS);
		}

		if (['firstName', 'lastName', 'clientIdn'].includes(config.type)) {
			if (!searchTerm || searchTerm.length < 2) {
				switch (config.type) {
					case 'firstName':
						return of(this.firstNameOptions || []);
					case 'lastName':
						return of(this.lastNameOptions || []);
					case 'clientIdn':
						return of(this.clientIdnOptions || []);
				}
			}
		} else {
			if (!searchTerm || searchTerm === '' || searchTerm.length < 3) {
				switch (config.type) {
					case 'device':
						return of(this.deviceOptions || []);
					case 'property':
						return of(this.propertiesOptions || []);
					case 'installationLocation':
						return of(this.locationInstallationOptions || []);
					case 'postalCode':
						return of(this.postalCodeOptions || []);
					case 'deviceGroup':
						return of(this.deviceGroupOptions || []);
				}
			}
		}

		const queryParameters: QueryParam[] = [
			{ param: this.getSearchParamName(config), value: searchTerm },
			{ param: 'rowIndex', value: 0 },
			{ param: 'rowCount', value: 100 },
		];

		return from(config.apiCall(queryParameters)).pipe(
			map((results) => this.processSearchResults(results, config))
		);
	}

	private processSearchResults(results: any[], config: SearchConfig): any[] {
		if (!results?.length) {
			return Array.from(config.selectedItems);
		}

		const newOptions = results.map(config.mapResponse);
		const mergedOptions = this.mergeWithSelected(
			newOptions,
			config.selectedItems
		);

		switch (config.type) {
			case 'device':
				this.deviceOptions = mergedOptions;
				break;
			case 'firstName':
				this.firstNameOptions = mergedOptions;
				break;
			case 'lastName':
				this.lastNameOptions = mergedOptions;
				break;
			case 'clientIdn':
				this.clientIdnOptions = mergedOptions;
				break;
			case 'property':
				this.propertiesOptions = mergedOptions;
				break;
			case 'installationLocation':
				this.locationInstallationOptions = mergedOptions;
				break;
			case 'postalCode':
				this.postalCodeOptions = mergedOptions;
				break;
			case 'deviceGroup':
				this.deviceGroupOptions = mergedOptions;
				break;
			case 'status':
				this.statusOptions = mergedOptions;
				break;
		}

		return mergedOptions;
	}

	private getSearchParamName(config: SearchConfig): string {
		switch (config.type) {
			case 'device':
				return 'serialNumber';
			case 'firstName':
				return 'firstName';
			case 'lastName':
				return 'lastName';
			case 'clientIdn':
				return 'idn';
			case 'property':
				return 'propertyName';
			case 'installationLocation':
				return 'installationLocationName';
			case 'postalCode':
				return 'postalCode';
			case 'deviceGroup':
				return 'name';
			case 'status':
				return 'status';
			default:
				return '';
		}
	}

	private mergeWithSelected(newOptions: any[], selectedItems: Set<any>): any[] {
		if (!newOptions) newOptions = [];
		if (!selectedItems) return newOptions;

		const newOptionIds = new Set(newOptions.map((opt) => opt.id));
		let mergedOptions = [...newOptions];

		selectedItems.forEach((selected) => {
			if (newOptionIds.has(selected.id)) {
				// if the selected item is already in the new options list, remove it from mergedOptions
				mergedOptions = mergedOptions.filter((opt) => opt.id !== selected.id);
			}
		});

		return mergedOptions;
	}

	onSearchInput(type: string, searchTerm: string): void {
		this.searchConfigs[type].searchEvents.next(searchTerm);
	}

	onSelectedItemsChanged(type: string, items: any): void {
		if (!items) return;

		const config = this.searchConfigs[type];
		config.selectedItems = new Set(items);
		config.updateFilters(items);
		config.searchEvents.next('');
	}

	async ngAfterViewInit(): Promise<void> {
		const userProfile: UserProfile = await this.apiService.getUserProfile();
		this.activeCompanyId = userProfile?.activeRoleDetails?.company?.id ?? '';
		const sentrilockId = await this.apiService.getSentrilockCompanyId();
		this.isSentrilock = this.activeCompanyId === sentrilockId;
	}

	get hasFilters(): boolean {
		return (
			JSON.stringify(this.inventoryReportFilters) !==
			JSON.stringify(emptyInventoryReportFilters())
		);
	}

	get primaryButtonText(): string {
		const {
			showAdvancedFilters,
			deviceGroupIds,
			...currentFiltersWithoutAdvancedOrGroupIds
		} = this.inventoryReportFilters;

		const {
			showAdvancedFilters: _,
			deviceGroupIds: __,
			...emptyFiltersWithoutAdvancedOrGroupIds
		} = emptyInventoryReportFilters();

		const showGenerateWithoutFiltersText =
			JSON.stringify(currentFiltersWithoutAdvancedOrGroupIds) !==
			JSON.stringify(emptyFiltersWithoutAdvancedOrGroupIds);

		if (showGenerateWithoutFiltersText) {
			return $localize`:@@inventoryReportDialog-generate:Generate`;
		} else {
			// Check if there are any "advanced filter" options
			const deviceGroupIdsPresent =
				(this.inventoryReportFilters.deviceGroupIds || [])?.length > 0;
			if (
				this.inventoryReportFilters.showAdvancedFilters &&
				deviceGroupIdsPresent
			) {
				return $localize`:@@inventoryReportDialog-generate:Generate`;
			}
			return $localize`:@@inventoryReportDialog-generateWithoutFilters:Generate without Filters`;
		}
	}

	onSelectedDevicesEvent(device: any): void {
		if (device === undefined) {
			return;
		}
		this.inventoryReportFilters.deviceIds = [...device];
	}

	onSelectedPropertyEvent(properties: any): void {
		if (properties === undefined) {
			return;
		}
		this.inventoryReportFilters.propertyNames = [...properties];
	}

	onSelectedDeviceGroupEvent(deviceGroups: any): void {
		if (deviceGroups === undefined) {
			return;
		}
		this.inventoryReportFilters.deviceGroupIds = [...deviceGroups];
	}

	onSelectedInstallLocationEvent(locations: any): void {
		if (locations === undefined) {
			return;
		}
		this.inventoryReportFilters.installationNames = [...locations];
	}

	onSelectedPostalCodeEvent(postalCodes: any): void {
		if (postalCodes === undefined) {
			return;
		}
		this.inventoryReportFilters.postalCodes = [...postalCodes];
	}

	onSelectedStatusEvent(status: any): void {
		if (status === undefined) {
			return;
		}
		this.inventoryReportFilters.statuses = [...status];
	}

	onStartDateChanged(event: MatDatepickerInputEvent<Date>): void {
		this.inventoryReportFilters.startDate = event.value ?? undefined;
	}

	onEndDateChanged(event: MatDatepickerInputEvent<Date>): void {
		this.inventoryReportFilters.endDate = event.value ?? undefined;
	}

	onCloseDialog(): void {
		this.dialogRef.close();
	}

	async onGenerateReport(): Promise<void> {
		const compiledFilters = this.compileFilters();
		this.router
			.navigateByUrl(`/reports/device-report`, {
				state: {
					reportFilters: this.inventoryReportFilters,
					compiledFilters,
					hasFilters: this.hasFilters,
					filterOptions: this.filterOptions,
				},
			})
			.then(() => {
				this.dialogRef.close();
			});
	}

	compileFilters(): QueryParam[] {
		const queryParameters: QueryParam[] = [];

		if (
			this.inventoryReportFilters.deviceIds &&
			this.inventoryReportFilters.deviceIds.length > 0
		) {
			queryParameters.push({
				param: 'deviceIds',
				value: this.inventoryReportFilters.deviceIds.map((it) => it.id),
			});
		}

		if (this.inventoryReportFilters.statuses) {
			queryParameters.push({
				param: 'status',
				value: this.inventoryReportFilters.statuses.map(
					(it) => it.status || ''
				),
			});
		}

		if (this.inventoryReportFilters.firstName) {
			queryParameters.push({
				param: 'firstNames',
				value: this.inventoryReportFilters.firstName.map(
					(it) => it.firstName || ''
				),
			});
		}

		if (this.inventoryReportFilters.lastName) {
			queryParameters.push({
				param: 'lastNames',
				value: this.inventoryReportFilters.lastName.map(
					(it) => it.lastName || ''
				),
			});
		}

		if (this.inventoryReportFilters.clientIdn) {
			queryParameters.push({
				param: 'clientIdns',
				value: this.inventoryReportFilters.clientIdn.map((it) => it.idn || ''),
			});
		}

		if (
			this.inventoryReportFilters.propertyNames &&
			this.inventoryReportFilters.propertyNames.length > 0
		) {
			queryParameters.push({
				param: 'propertyNames',
				value: this.inventoryReportFilters.propertyNames.map(
					(it) => it.propertyName
				),
			});
		}

		if (this.inventoryReportFilters.installationNames) {
			queryParameters.push({
				param: 'installationLocationName',
				value: this.inventoryReportFilters.installationNames.map(
					(installName) => installName.locationInstallationName
				),
			});
		}

		if (this.inventoryReportFilters.postalCodes) {
			queryParameters.push({
				param: 'postalCode',
				value: this.inventoryReportFilters.postalCodes.map(
					(pc) => pc.postalCode
				),
			});
		}

		if (
			!!this.inventoryReportFilters.showAdvancedFilters &&
			this.inventoryReportFilters.deviceGroupIds &&
			this.inventoryReportFilters.deviceGroupIds.length > 0
		) {
			queryParameters.push({
				param: 'deviceGroupIds',
				value: this.inventoryReportFilters.deviceGroupIds.map((it) => it.id),
			});
		}

		if (this.inventoryReportFilters.startDate) {
			queryParameters.push({
				param: 'dateChangedUtcStart',
				value: this.inventoryReportFilters.startDate
					.toISOString()
					.split('T')[0],
			});
		}

		if (this.inventoryReportFilters.endDate) {
			queryParameters.push({
				param: 'dateChangedUtcEnd',
				value: this.inventoryReportFilters.endDate.toISOString().split('T')[0],
			});
		}
		return queryParameters;
	}

	ngOnDestroy(): void {
		Object.values(this.searchConfigs).forEach((config) => {
			if (config.subscription) {
				config.subscription.unsubscribe();
			}
		});
	}
}
